import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';
import { BadgePlusIcon, Loader2Icon } from 'lucide-react';

import { usePwa } from '~/states/pwa';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

function ReloadPrompt() {
  const [isUpdating, setIsUpdating] = useState(false);
  const { needRefresh, updateServiceWorker } = usePwa();

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateServiceWorker(true);
    } catch (error) {
      console.error('Failed to update:', error);
      Sentry.captureException(error, {
        tags: { update_error: 'update_service_worker' },
      });
      setIsUpdating(false);
    }
  };

  return (
    <>
      {needRefresh && (
        <Popover>
          <Tooltip
            defaultOpen
            onOpenChange={(open, eventDetails) => {
              if (!open && eventDetails.reason === 'outside-press') {
                eventDetails.cancel();
              }
            }}
          >
            <TooltipTrigger
              className="disabled:pointer-events-auto"
              render={
                <PopoverTrigger
                  render={
                    <Button
                      size="icon-responsive"
                      variant="ghost"
                      aria-label={t`Update available`}
                    />
                  }
                />
              }
            >
              <BadgePlusIcon size={18} />
            </TooltipTrigger>
            <TooltipContent sideOffset={-2}>
              <p>
                <Trans>Update available</Trans>
              </p>
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            className="mr-3 flex w-min max-w-96 min-w-44 flex-col items-center justify-center gap-2 sm:mr-8"
            sideOffset={5}
          >
            <h3 className="text-center leading-none font-medium text-nowrap">
              <Trans>Ready to update</Trans>
            </h3>
            <p className="mb-1 shrink-0 text-center text-sm leading-none font-light text-balance break-keep">
              <Trans>Refresh to get the latest improvements.</Trans>
            </p>
            <Button onClick={handleUpdate} disabled={isUpdating}>
              {isUpdating ? <Loader2Icon className="animate-spin" /> : null}
              <Trans>Update now</Trans>
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

export default ReloadPrompt;
